import { useCallback, useState } from 'react'
import baseService from '../../services/baseService'
import useErrorHandling from '../useErrorHandling'
import useNotification from '../useNotification'
import { forEach, uniq } from 'lodash'

const useBaseService = (navigate = false) => {
  const { handleError } = useErrorHandling(navigate) // Hook para tratamento de erros.
  const { showNotification } = useNotification() // Hook para exibir notificações.
  const [formErrors, setFormErrors] = useState({}) // Estado para armazenar erros de formulário.

  /**
   * Função para limpar os erros do formulário.
   */
  const clearFormErrors = useCallback(() => {
    setFormErrors({})
  }, [])

  /**
   * Função para realizar a requisição de qualquer tipo (GET, POST, PUT, DELETE), com tratamento de sucesso ou erro.
   *
   * @param {function} requestFn - Função que realiza a requisição (como baseService.get, baseService.post, etc.).
   * @param {string|null} successMessage - Mensagem de sucesso a ser exibida, se fornecida.
   * @param {string|null} customErrorMessage - Mensagem de erro personalizada, caso haja erro na requisição.
   *
   * @returns {Promise} - Retorna a resposta da requisição ou lida com erro, se houver.
   */
  const handleRequest = useCallback(
    async (requestFn, successMessage, customErrorMessage, options = {}) => {
      const { skipNotification = false } = options
      clearFormErrors() // Limpa erros anteriores de formulário.
      try {
        const response = await requestFn() // Executa a requisição.
        if (successMessage && !skipNotification) {
          showNotification('success', response.message || successMessage) // Exibe notificação de sucesso, se houver mensagem.
        }
        return response
      } catch (error) {
        // Caso ocorra erro, verifica o tipo de erro.
        if (error.status === 422) {
          // Para casos de chamadas para o equipamentos -> find-by-numbers
          if (
            error.data['numbers.0'] &&
            error.data['numbers.0'].startsWith('O equipamento de número')
          ) {
            showNotification('warning', error.data['numbers.0'])
            return
          }
          if (
            error.data['serials.0'] &&
            error.data['serials.0'].startsWith('O equipamento de serial')
          ) {
            showNotification('warning', error.data['serials.0'])
            return
          }
          if (
            error.data['volumes'] &&
            error.data['volumes'].includes('The volumes field is required.')
          ) {
            showNotification(
              'warning',
              'Não existem volumes! Crie um novo volume e adicione insumos'
            )
            return
          }
          console.log(error.data)
          setFormErrors(error.data || {}) // Atualiza erros de formulário.
          // TODO: Removido, para que seja exibido a mensagem de error exato que vem do backend
          // showNotification('warning', 'Verifique os campos destacados.') // Exibe notificação de aviso.
          const messages = Array.isArray(error?.data)
            ? error.data
            : Object.values(error?.data ?? {}).flat()

          forEach(uniq(messages), (message) => {
            showNotification('warning', message)
          })
          throw error // Lança o erro para ser tratado em outro lugar, se necessário.
        }
        console.log(error)
        handleError(error, customErrorMessage) // Lida com erro genérico.
        throw error // Lança o erro para ser tratado em outro lugar, se necessário.
      }
    },
    [handleError, showNotification, clearFormErrors]
  )

  /**
   * Função para realizar requisição GET com parâmetros e filtros opcionais.
   *
   * @param {string} url - URL da requisição.
   * @param {object} params - Parâmetros para a requisição.
   * @param {string|null} entity - Nome da entidade (opcional).
   *
   * @returns {Promise} - Retorna a resposta da requisição.
   */
  const get = useCallback(
    async (url, params = {}, headers = {}, entity = null) =>
      handleRequest(
        () => baseService.get(url, params, headers, entity, navigate),
        null,
        'Erro ao buscar os dados.'
      ),
    [navigate, handleRequest]
  )

  /**
   * Função para realizar requisição GET baseada em uma coluna específica.
   *
   * @param {string} url - URL da requisição.
   *
   * @returns {Promise} - Retorna a resposta da requisição.
   */
  const getByColumn = useCallback(
    async (url) =>
      handleRequest(
        () => baseService.getByColumn(url, navigate),
        null,
        'Erro ao buscar os dados.'
      ),
    [navigate, handleRequest]
  )

  /**
   * Função para realizar requisição POST para cadastrar dados.
   *
   * @param {string} url - URL da requisição.
   * @param {object} data - Dados a serem enviados no corpo da requisição.
   *
   * @returns {Promise} - Retorna a resposta da requisição.
   */
  const post = useCallback(
    async (url, data, options = {}) => {
      const { skipNotification = false, ...requestOptions } = options
      return handleRequest(
        () => baseService.post(url, data, navigate, requestOptions),
        'Cadastro realizado com sucesso.',
        'Erro ao cadastrar.',
        { skipNotification }
      )
    },
    [navigate, handleRequest]
  )

  /**
   * Função para realizar requisição PUT para editar dados.
   *
   * @param {string} url - URL da requisição.
   * @param {object} data - Dados a serem enviados no corpo da requisição.
   *
   * @returns {Promise} - Retorna a resposta da requisição.
   */
  const put = useCallback(
    async (url, data) =>
      handleRequest(
        () => baseService.put(url, data, navigate),
        'Edição realizada com sucesso.',
        'Erro ao editar.'
      ),
    [navigate, handleRequest]
  )

  /**
   * Função para realizar requisição DELETE para excluir dados.
   *
   * @param {string} url - URL da requisição.
   *
   * @returns {Promise} - Retorna a resposta da requisição.
   */
  const del = useCallback(
    async (url, config) =>
      handleRequest(
        () => baseService.delete(url, config, navigate),
        'Exclusão realizada com sucesso.',
        'Erro ao excluir.'
      ),
    [navigate, handleRequest]
  )

  const deleteWithParams = useCallback(
    async (url, params, headers = {}) =>
      handleRequest(
        () => baseService.deleteWithParams(url, params, headers, navigate),
        'Exclusão realizada com sucesso.',
        'Erro ao excluir.'
      ),
    [navigate, handleRequest]
  )

  // Retorna os hooks e funções que podem ser utilizados em outros componentes.
  return {
    get,
    getByColumn,
    post,
    put,
    del,
    deleteWithParams,
    formErrors, // Retorna os erros do formulário.
    setFormErrors, // Função para atualizar os erros do formulário diretamente.
  }
}

export default useBaseService
